import { useState } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getCroppedImg } from '@/lib/cropImage'
import { createUserFacingError, formatUserFacingError } from '@/lib/errors'
import type { MediaDocument } from '../../services/admin.media.service'

type AspectKey = 'free' | '1:1' | '16:9' | '4:3' | '9:16' | '3:4'

const ASPECT_RATIOS: { label: string; key: AspectKey; ratio: number | undefined }[] = [
  { label: 'Free', key: 'free', ratio: undefined },
  { label: '1:1', key: '1:1', ratio: 1 },
  { label: '16:9', key: '16:9', ratio: 16 / 9 },
  { label: '4:3', key: '4:3', ratio: 4 / 3 },
  { label: '9:16', key: '9:16', ratio: 9 / 16 },
  { label: '3:4', key: '3:4', ratio: 3 / 4 },
]

interface MediaCropModalProps {
  open: boolean
  item: MediaDocument
  onClose: () => void
  onSaveAsCopy: (blob: Blob) => Promise<void>
  onOverwrite: (blob: Blob) => Promise<void>
}

export function MediaCropModal({
  open,
  item,
  onClose,
  onSaveAsCopy,
  onOverwrite,
}: MediaCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [aspectKey, setAspectKey] = useState<AspectKey>('free')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentAspect = ASPECT_RATIOS.find((r) => r.key === aspectKey)

  async function getBlob(): Promise<Blob> {
    if (!croppedAreaPixels) throw createUserFacingError('Choose a crop area before continuing.')
    return getCroppedImg(item.url, croppedAreaPixels, croppedAreaPixels.width, croppedAreaPixels.height)
  }

  async function handleSaveAsCopy() {
    setSaving(true)
    setError(null)
    try {
      const blob = await getBlob()
      await onSaveAsCopy(blob)
      onClose()
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to crop image.'))
    } finally {
      setSaving(false)
    }
  }

  async function handleOverwrite() {
    setSaving(true)
    setError(null)
    try {
      const blob = await getBlob()
      await onOverwrite(blob)
      onClose()
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to crop image.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Crop Image</DialogTitle>
          <DialogDescription>
            Adjust the crop area, then save as a new copy or overwrite the original.
          </DialogDescription>
        </DialogHeader>

        {/* Aspect ratio presets */}
        <div className="flex flex-wrap gap-1.5">
          {ASPECT_RATIOS.map((r) => (
            <Button
              key={r.key}
              variant={aspectKey === r.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAspectKey(r.key)}
              disabled={saving}
            >
              {r.label}
            </Button>
          ))}
        </div>

        {/* Crop area */}
        <div className="relative h-72 w-full overflow-hidden rounded-md border bg-muted">
          <Cropper
            image={item.url}
            crop={crop}
            zoom={zoom}
            aspect={currentAspect?.ratio}
            objectFit="contain"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
          />
        </div>

        {/* Zoom slider */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Zoom</Label>
          <Slider
            min={1}
            max={3}
            step={0.01}
            value={[zoom]}
            onValueChange={(vals) => setZoom(vals[0] ?? 1)}
            disabled={saving}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => void handleSaveAsCopy()}
            disabled={saving || !croppedAreaPixels}
          >
            {saving ? 'Saving…' : 'Save as Copy'}
          </Button>
          <Button
            onClick={() => void handleOverwrite()}
            disabled={saving || !croppedAreaPixels}
          >
            {saving ? 'Saving…' : 'Overwrite Original'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
